/**
 * Wellness check-in validation.
 *
 * Mirrors the API's `WellnessEntryRequest`: four integer ratings from 1 to 7,
 * an optional list of sore muscles, an optional note of at most 530
 * characters, and an optional date that may not be in the future.
 */

import { singleDateSchema } from './date.schemas';
import { z } from './zod';
import {
  WELLNESS_NOTES_MAX,
  WELLNESS_SCALE,
} from '../types/domain/wellness.types';
import { dayjs } from '../utils/date';

const ratingSchema = z
  .number({ message: 'Pick a rating from 1 to 7' })
  .int('Ratings are whole numbers')
  .min(
    WELLNESS_SCALE.min,
    `Ratings run from ${WELLNESS_SCALE.min} to ${WELLNESS_SCALE.max}`
  )
  .max(
    WELLNESS_SCALE.max,
    `Ratings run from ${WELLNESS_SCALE.min} to ${WELLNESS_SCALE.max}`
  );

export const wellnessEntrySchema = z.object({
  date: singleDateSchema
    .refine(
      (value) => !dayjs(value).isAfter(dayjs(), 'day'),
      'A check-in cannot be dated in the future'
    )
    .optional(),
  fatigue: ratingSchema,
  soreness: ratingSchema,
  stress: ratingSchema,
  sleep_quality: ratingSchema,
  sore_muscles: z.array(z.string().min(1)).optional(),
  notes: z
    .string()
    .trim()
    .max(
      WELLNESS_NOTES_MAX,
      `Notes are at most ${WELLNESS_NOTES_MAX} characters`
    )
    .optional(),
});

export type WellnessEntrySchemaInput = z.input<typeof wellnessEntrySchema>;
export type WellnessEntrySchemaOutput = z.output<typeof wellnessEntrySchema>;

export function validateWellnessEntry(
  data: unknown
): WellnessEntrySchemaOutput {
  return wellnessEntrySchema.parse(data);
}

export function safeValidateWellnessEntry(
  data: unknown
): z.ZodSafeParseResult<WellnessEntrySchemaOutput> {
  return wellnessEntrySchema.safeParse(data);
}
